import React from "react";
import { UseFormRegisterReturn, FieldError } from "react-hook-form";
import { Tooltip } from "react-tooltip";

interface Option {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectFieldProps {
  label: string;
  options: Option[];
  loading?: boolean;
  placeholder?: string;
  error?: FieldError;
  disabled?: boolean;
  className?: string;
  register?: UseFormRegisterReturn;
  helpText?: string;
  tooltip?: string;
}

export function SelectField({
  label,
  options,
  loading = false,
  placeholder,
  error,
  disabled = false,
  className = "",
  register,
  helpText,
  tooltip,
}: SelectFieldProps) {
  const fieldId = register?.name ? `field-${register.name}` : `field-${label.replace(/\s+/g, "-")}`;
  const errorId = `${fieldId}-error`;
  const helpTextId = `${fieldId}-help`;
  const tooltipId = `tooltip-${label.replace(/\s+/g, "-")}`;

  const describedBy = [
    helpText ? helpTextId : null,
    error ? errorId : null,
  ].filter(Boolean).join(" ") || undefined;

  return (
    <div className={className}>
      <label htmlFor={fieldId} className="flex items-center gap-1.5 text-gray-bb-1 font-medium mb-1.5 sm:mb-2 text-sm sm:text-base">
        {label}
        {tooltip && (
          <>
            <span
              data-tooltip-id={tooltipId}
              className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-bb text-white-bb text-xs font-bold cursor-help shrink-0"
              aria-label="Ajuda"
            >
              ?
            </span>
            <Tooltip
              id={tooltipId}
              place="top"
              className="!max-w-xs !whitespace-pre-line !z-50"
            >
              {tooltip}
            </Tooltip>
          </>
        )}
      </label>
      {helpText && (
        <p id={helpTextId} className="text-gray-bb-2 text-xs sm:text-sm mb-2 uppercase">{helpText}</p>
      )}
      <select
        {...register}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={`w-full border rounded-lg p-2.5 sm:p-3 text-sm sm:text-base focus:ring-2 focus:ring-blue-bb focus:border-transparent transition-all ${
          error ? "border-red-500" : "border-gray-bb-4"
        }`}
        disabled={loading || disabled}
      >
        <option value="">
          {loading ? "Carregando..." : placeholder || "Selecione..."}
        </option>
        {options.map((option, index) => (
          <option key={index} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <p id={errorId} role="alert" className="text-red-500 text-xs sm:text-sm mt-1">{error.message}</p>
      )}
    </div>
  );
}
